export interface RecentItem {
  id: number;
  type: 'claim' | 'tow-request';
  label: string; // e.g. claim number
  visitedAt: number; // Unix ms
  /**
   * Tow requests only. `/tow-requests/:id` is ambiguous — the id addresses a
   * `CmTowRequest` when this is `TOWING` and an `SrServiceRequest` when it is
   * `ROADSIDE ASSISTANCE`, and `Get-Details` marks the discriminator
   * `[Required]`. Recording it here keeps a recent entry openable; without it
   * the menu produced a bare `/tow-requests/:id` that always 400'd.
   *
   * Optional so entries stored before this field existed still parse.
   */
  serviceType?: string;
}

/** Path for a recent entry, carrying the tow-request discriminator when present. */
export function recentItemPath(item: RecentItem): string {
  if (item.type === 'claim') return `/claims/${item.id}`;
  const qs = item.serviceType
    ? `?serviceType=${encodeURIComponent(item.serviceType)}`
    : '';
  return `/tow-requests/${item.id}${qs}`;
}

const KEY = 'itow.preferences.recentItems';
const MAX = 8;

export function trackRecent(item: Omit<RecentItem, 'visitedAt'>): void {
  try {
    const raw = localStorage.getItem(KEY);
    const list: RecentItem[] = raw ? JSON.parse(raw) : [];
    const filtered = list.filter((i) => !(i.type === item.type && i.id === item.id));
    const next: RecentItem[] = [{ ...item, visitedAt: Date.now() }, ...filtered].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (private browsing, storage quota) — fail silently.
  }
  // Same-tab listeners — `storage` only fires in *other* tabs.
  window.dispatchEvent(new Event('itow-recents-changed'));
}

export function getRecent(): RecentItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function clearRecent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // localStorage may be unavailable (private browsing) — fail silently.
  }
  window.dispatchEvent(new Event('itow-recents-changed'));
}
